import React, { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronsUpDown,
  Copy,
  MoreHorizontal,
  Power,
  QrCode,
  RefreshCw,
  Trash2,
  Upload,
} from 'lucide-react';
import { toast } from 'react-toastify';

import { cn } from '../../../lib/utils';
import { Button } from '../../ui/button';
import AdminSelect from '../../common/AdminSelect';
import PaperworkChip from './PaperworkChip';
import {
  useFetchPartners,
  useUpdatePartner,
  useDeletePartner,
  useUploadPartnerPaperwork,
} from '../../../services/partners';

import PartnerQrModal from './PartnerQrModal';
import { ConfirmModal, UploadPaperworkModal } from './ReviewActionModals';
import { partnerUrlFor } from './partnerHelpers';

const PAGE_SIZE = 25;

const CHIP =
  'inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium';

// One shimmering placeholder row while the first page loads.
const SkeletonRow = () => (
  <tr className="border-b border-border last:border-0">
    {[36, 44, 32, 24, 28, 16, 8].map((w, i) => (
      <td key={i} className="px-4 py-3">
        <div
          className="h-4 animate-pulse rounded-md bg-muted"
          style={{ maxWidth: `${w * 4}px` }}
        />
      </td>
    ))}
  </tr>
);

// The individual referral partners list (organizations are filtered out —
// see PartnersPage). Search/include-inactive live in the page header; this
// owns paging, sorting and the row actions.
const PartnersTable = ({
  search,
  includeInactive,
  canManage,
  canViewPaperwork,
}) => {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState('partnerName');
  const [sortDir, setSortDir] = useState('asc');
  const [menuFor, setMenuFor] = useState(null); // partnerId of the open action menu
  const [qrFor, setQrFor] = useState(null);
  const [uploadFor, setUploadFor] = useState(null);
  const [deleting, setDeleting] = useState(null);
  // partnerId of the open Lead-routing picker (internal-only CM assignment).
  const [routingFor, setRoutingFor] = useState(null);

  // New search / inactive toggle restarts paging.
  useEffect(() => {
    setPage(1);
  }, [search, includeInactive]);

  const { data, isLoading, isError, refetch } = useFetchPartners(
    {
      search: search || undefined,
      page,
      limit: PAGE_SIZE,
      sortBy,
      sortDir,
      includeInactive: includeInactive ? 'true' : 'false',
      // Individuals only: organizations (API lead partners + the internal
      // Referral Program umbrella) are deliberately not managed on this page.
      type: 'individual',
    },
    { keepPreviousData: true },
  );
  const rows = data?.rows || [];
  const total = data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const firstShown = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const lastShown = Math.min(page * PAGE_SIZE, total);

  // A mutation can empty the current page (delete the last row on page 2) —
  // snap back to the last real page instead of showing a false empty state.
  useEffect(() => {
    if (!isLoading && page > 1 && rows.length === 0 && total > 0) {
      setPage(Math.min(page - 1, totalPages));
    }
  }, [isLoading, page, rows.length, total, totalPages]);

  const invalidate = () => queryClient.invalidateQueries(['partners']);
  const onError = (err) => toast.error(err?.response?.data?.msg || err.message);

  const updatePartner = useUpdatePartner(invalidate, { onError });
  const deletePartner = useDeletePartner(
    () => {
      toast.success('Partner deleted.');
      setDeleting(null);
      invalidate();
    },
    {
      // Surface the server's message (409 = linked payee/payouts refuse the
      // delete). 403 already gets the global permission-denied toast.
      onError: (err) => {
        setDeleting(null);
        if (err.response?.status !== 403) onError(err);
      },
    },
  );
  const uploadPaperwork = useUploadPartnerPaperwork(
    () => {
      toast.success('Paperwork uploaded.');
      setUploadFor(null);
    },
    { onError },
  );

  const toggleActive = (partner) => {
    updatePartner.mutate(
      {
        partnerId: partner.partnerId,
        payload: { partnerActive: !partner.partnerActive },
      },
      {
        onSuccess: () =>
          toast.success(
            partner.partnerActive
              ? 'Partner deactivated.'
              : 'Partner reactivated.',
          ),
      },
    );
  };

  // Lead routing: which CM this partner's leads (and their claims) go to.
  // Internal-only — the partner never sees any of this. Immediate save on
  // pick; clearing falls back to the system default assignee.
  const saveRouting = (partner, userId) => {
    updatePartner.mutate(
      {
        partnerId: partner.partnerId,
        payload: { partnerAssignReferralsTo: userId },
      },
      {
        onSuccess: () =>
          toast.success(
            userId
              ? 'Lead routing updated.'
              : 'Lead routing cleared — system default applies.',
          ),
      },
    );
    setRoutingFor(null);
  };

  const copyLink = async (slug) => {
    try {
      await navigator.clipboard.writeText(partnerUrlFor(slug));
      toast.success('Link copied!');
    } catch (e) {
      toast.error('Could not copy — please copy it manually.');
    }
  };

  const toggleSort = (key) => {
    if (sortBy === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortDir('asc');
    }
    setPage(1);
  };

  // Sortable header cell (LeadHistory recipe). No sortKey → static header.
  const Th = ({ children, sortKey, className }) => {
    const active = sortKey && sortBy === sortKey;
    const Icon = !sortKey
      ? null
      : active
        ? sortDir === 'asc'
          ? ChevronUp
          : ChevronDown
        : ChevronsUpDown;
    return (
      <th
        className={cn(
          'px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wide text-muted-foreground',
          className,
        )}
      >
        {sortKey ? (
          <button
            type="button"
            onClick={() => toggleSort(sortKey)}
            className={cn(
              'inline-flex items-center gap-1 hover:text-foreground',
              active && 'text-foreground',
            )}
          >
            {children}
            {Icon ? <Icon className="h-3.5 w-3.5" /> : null}
          </button>
        ) : (
          children
        )}
      </th>
    );
  };

  const menuItem = (onClick, Icon, label, danger = false) => (
    <button
      type="button"
      onClick={() => {
        setMenuFor(null);
        onClick();
      }}
      className={cn(
        'flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm transition-colors hover:bg-accent',
        danger ? 'text-status-error-fg' : 'text-foreground',
      )}
    >
      <Icon className="h-4 w-4" /> {label}
    </button>
  );

  return (
    <>
      <div className="overflow-visible rounded-xl border border-border bg-card">
        <table className="w-full text-sm">
          <thead className="bg-card">
            <tr className="border-b border-border">
              <Th sortKey="partnerName">Partner</Th>
              <Th>Referral link</Th>
              <Th>Paperwork</Th>
              <Th>Lead routing</Th>
              <Th>Performance</Th>
              <Th className="text-center">Status</Th>
              <Th className="text-right">Actions</Th>
            </tr>
          </thead>
          <tbody>
            {isError ? (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center">
                  <div className="flex flex-col items-center gap-3">
                    <AlertTriangle className="h-6 w-6 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">
                      Could not load partners.
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => refetch()}
                    >
                      <RefreshCw className="mr-2 h-4 w-4" /> Retry
                    </Button>
                  </div>
                </td>
              </tr>
            ) : isLoading && !data ? (
              Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)
            ) : rows.length === 0 ? (
              <tr>
                <td
                  colSpan={7}
                  className="px-4 py-10 text-center text-muted-foreground"
                >
                  {search
                    ? 'No partners match this search.'
                    : 'No partners yet.'}
                </td>
              </tr>
            ) : (
              rows.map((p) => {
                const isIndividual = p.partnerType === 'individual';
                const docs = p.paperwork || [];
                return (
                  <tr
                    key={p.partnerId}
                    className={cn(
                      'border-b border-border last:border-0 hover:bg-accent/40',
                      !p.partnerActive && 'opacity-60',
                    )}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-foreground">
                          {p.partnerName}
                        </span>
                        {/* The page shows individuals only (type=individual
                            fetch) — a per-row type chip would be pure noise.
                            Defensive chip ONLY if an org ever slips through. */}
                        {!isIndividual ? (
                          <span
                            className={cn(
                              CHIP,
                              'bg-status-info-bg text-status-info-fg',
                            )}
                          >
                            Organization
                          </span>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <span className="text-foreground">
                          /partner/{p.partnerReferralUrl}
                        </span>
                        <button
                          type="button"
                          onClick={() => copyLink(p.partnerReferralUrl)}
                          title="Copy referral link"
                          className="rounded p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                        >
                          <Copy className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      {p.partnerCode ? (
                        <div
                          className="text-xs text-muted-foreground"
                          title="Scan-tracked short link — what this partner's QR code encodes. Point legacy qrco.de links here."
                        >
                          QR link: /r/{p.partnerCode}
                        </div>
                      ) : null}
                    </td>
                    <td className="px-4 py-3">
                      {/* Compact: one chip, docs behind a click. "Signed" when
                          paperwork is on file (e-signed or uploaded); "Legacy"
                          for individuals whose old-era paperwork hasn't been
                          uploaded yet; em-dash for organizations. */}
                      {!isIndividual ? (
                        <span className="text-muted-foreground">—</span>
                      ) : docs.length === 0 ? (
                        <span
                          className={cn(CHIP, 'bg-muted text-muted-foreground')}
                          title="Paperwork was signed outside the system — upload it from the actions menu to attach it here."
                        >
                          Legacy
                        </span>
                      ) : (
                        <PaperworkChip docs={docs} canView={canViewPaperwork} />
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {/* Lead routing: the CM this partner's leads (and the
                          claims they become) are assigned to. Internal only —
                          nothing here is partner-facing. */}
                      {canManage ? (
                        <div className="relative inline-block">
                          <button
                            type="button"
                            onClick={() =>
                              setRoutingFor((cur) =>
                                cur === p.partnerId ? null : p.partnerId,
                              )
                            }
                            title="Choose which Claims Manager this partner's leads are assigned to"
                            className={cn(
                              CHIP,
                              'cursor-pointer transition-shadow hover:ring-1 hover:ring-border',
                              p.partnerAssignReferralsTo
                                ? 'bg-status-info-bg text-status-info-fg'
                                : 'bg-muted text-muted-foreground',
                            )}
                          >
                            {p.partnerAssignReferralsToName || 'Default'}
                            <ChevronDown className="ml-1 h-3 w-3" />
                          </button>
                          {routingFor === p.partnerId ? (
                            <>
                              <button
                                type="button"
                                aria-hidden="true"
                                tabIndex={-1}
                                onMouseDown={(e) => e.preventDefault()}
                                className="fixed inset-0 z-40 cursor-default"
                                onClick={() => setRoutingFor(null)}
                              />
                              <div className="absolute left-0 top-7 z-50 w-72 rounded-xl border border-border bg-card p-3 text-left shadow-lg">
                                <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                                  Assign this partner&apos;s leads to
                                </div>
                                <AdminSelect
                                  value={
                                    p.partnerAssignReferralsTo
                                      ? {
                                          label: p.partnerAssignReferralsToName,
                                          value: p.partnerAssignReferralsTo,
                                        }
                                      : null
                                  }
                                  showDefaultOptions
                                  showUserType={false}
                                  onChange={(opt) =>
                                    saveRouting(p, opt ? opt.value : null)
                                  }
                                />
                                <p className="mt-2 text-xs text-muted-foreground">
                                  New leads and their claims go to this person.
                                  Clear it to use the system default.
                                </p>
                              </div>
                            </>
                          ) : null}
                        </div>
                      ) : (
                        <span
                          className={
                            p.partnerAssignReferralsToName
                              ? 'text-foreground'
                              : 'text-muted-foreground'
                          }
                        >
                          {p.partnerAssignReferralsToName || 'Default'}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {/* Funnel at a glance: QR scans -> page visits -> leads ->
                          converted clients. The payout-dollar suffix was
                          removed 2026-08-23 (Nic: no payout totals on this
                          page; Referral Payouts is the money surface). */}
                      <div className="text-xs text-muted-foreground">
                        {p.statScans ?? 0} scans · {p.statVisits ?? 0} visits
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {p.statLeads ?? 0} leads · {p.statConversions ?? 0}{' '}
                        {(p.statConversions ?? 0) === 1 ? 'client' : 'clients'}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span
                        className={cn(
                          CHIP,
                          p.partnerActive
                            ? 'bg-status-success-bg text-status-success-fg'
                            : 'bg-muted text-muted-foreground',
                        )}
                      >
                        {p.partnerActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="relative inline-flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => setQrFor(p)}
                          title="View QR code"
                          className="rounded-md border border-border p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                        >
                          <QrCode className="h-4 w-4" />
                        </button>
                        {canManage ? (
                          <>
                            <button
                              type="button"
                              onClick={() =>
                                setMenuFor((cur) =>
                                  cur === p.partnerId ? null : p.partnerId,
                                )
                              }
                              title="More actions"
                              className="rounded-md border border-border p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </button>
                            {menuFor === p.partnerId ? (
                              <>
                                <button
                                  type="button"
                                  aria-hidden="true"
                                  tabIndex={-1}
                                  onMouseDown={(e) => e.preventDefault()}
                                  className="fixed inset-0 z-40 cursor-default"
                                  onClick={() => setMenuFor(null)}
                                />
                                <div className="absolute right-0 top-9 z-50 w-52 rounded-xl border border-border bg-card p-1 text-left shadow-lg">
                                  {/* No Edit here: an individual's name/details
                                      come from their signed paperwork. */}
                                  {isIndividual
                                    ? menuItem(
                                        () => setUploadFor(p),
                                        Upload,
                                        'Upload paperwork',
                                      )
                                    : null}
                                  {menuItem(
                                    () => toggleActive(p),
                                    Power,
                                    p.partnerActive
                                      ? 'Deactivate'
                                      : 'Reactivate',
                                  )}
                                  <div className="mx-3 my-1 h-px bg-border" />
                                  {menuItem(
                                    () => setDeleting(p),
                                    Trash2,
                                    'Delete',
                                    true,
                                  )}
                                </div>
                              </>
                            ) : null}
                          </>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pager */}
      <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
        <span>
          {total === 0
            ? 'No partners'
            : `Showing ${firstShown}–${lastShown} of ${total}`}
        </span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="rounded-md border border-border p-1 hover:bg-accent disabled:opacity-40"
            aria-label="Previous page"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            className="rounded-md border border-border p-1 hover:bg-accent disabled:opacity-40"
            aria-label="Next page"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Row-action modals — keyed / conditionally mounted so state is fresh per partner. */}
      {qrFor ? (
        <PartnerQrModal partner={qrFor} onClose={() => setQrFor(null)} />
      ) : null}
      {uploadFor ? (
        <UploadPaperworkModal
          key={uploadFor.partnerId}
          title={`Upload paperwork — ${uploadFor.partnerName}`}
          description="Attach signed PDFs collected outside the e-sign flow (they'll appear with an (uploaded) marker)."
          onClose={() => setUploadFor(null)}
          onUpload={(files) =>
            uploadPaperwork.mutate({ partnerId: uploadFor.partnerId, files })
          }
          isSaving={uploadPaperwork.isLoading}
        />
      ) : null}
      {deleting ? (
        <ConfirmModal
          title="Delete partner"
          body={
            <>
              Delete <span className="font-medium">{deleting.partnerName}</span>
              ? This can&apos;t be undone. Partners with linked payouts
              can&apos;t be deleted — deactivate them instead.
            </>
          }
          confirmLabel="Delete"
          destructive
          onClose={() => setDeleting(null)}
          onConfirm={() => deletePartner.mutate(deleting.partnerId)}
          isSaving={deletePartner.isLoading}
        />
      ) : null}
    </>
  );
};

export default PartnersTable;
